const BigNumber = require('bignumber.js');

class SalesTotals {
    constructor() {
        /**
         * This object holds all the underlying data for the totals.
         * It starts as the structure of returned by makeBlankStatGroup().
         *
         * As totals are added they are added to the values, and additional keys are added to index by itemType,
         * salesType, and itemType + salesType.
         *
         * This is best explained with an example.
         * If we assume makeBlankStatGroup returned {lines: 0}
         * Then call
         * addToTotals('Ticket', 'Sale', 'lines', 1)
         * this.totals would look like:
         * {
         *     lines: 1,
         *     Ticket: {
         *         lines: 1,
         *         Sale: {
         *             lines: 1
         *         }
         *     },
         *     Sale: {
         *         lines: 1,
         *     }
         * }
         *
         * @type {{}}
         */
        this.totals = {};

        this.reset();
    }

    makeBlankStatGroup() {
        let stats = {};
        stats[SalesTotals.TOTAL_TYPES.ORDERS] = 0;
        stats[SalesTotals.TOTAL_TYPES.QTY] = 0;
        stats[SalesTotals.TOTAL_TYPES.LINES] = 0;
        stats[SalesTotals.TOTAL_TYPES.BASE_TOTAL] = 0;
        stats[SalesTotals.TOTAL_TYPES.FEES] = 0;
        stats[SalesTotals.TOTAL_TYPES.DISCOUNT] = 0;
        stats[SalesTotals.TOTAL_TYPES.LINE_TOTAL] = 0;
        stats[SalesTotals.TOTAL_TYPES.PEOPLE] = 0;
        stats[SalesTotals.TOTAL_TYPES.GIFT_AID_DONATION] = 0;
        return stats;
    }

    /**
     * @private
     *
     * Get a single total from a set of totals.
     *
     * @param {string|null} itemType
     * @param {string|null} saleType
     * @param {string} whichTotal
     *
     * @returns {number}
     */
    getTotal(itemType, saleType, whichTotal) {
        itemType = typeof itemType === 'undefined' ? null : itemType;
        saleType = typeof saleType === 'undefined' ? null : saleType;

        if (saleType) {
            this.validateSaleType(saleType);
        }

        // Only a single total is retrieved so that must exist in the totals set.
        this.validateTotalType(whichTotal);

        if (itemType != null && saleType != null) {
            // Both itemType and saleType are being asked for.
            return (this.totals.hasOwnProperty(itemType) && this.totals[itemType].hasOwnProperty(saleType))
                ? this.totals[itemType][saleType][whichTotal]
                : 0;
        } else if (itemType != null && saleType == null) {
            // Only the itemType total is being asked for.
            return (this.totals.hasOwnProperty(itemType)) ? this.totals[itemType][whichTotal] : 0;
        } else if (itemType == null && saleType != null) {
            // Only the saleType total is being asked for.
            return (this.totals.hasOwnProperty(saleType)) ? this.totals[saleType][whichTotal] : 0;
        }
        return this.totals[whichTotal];
    }

    /**
     * Add a value to all the applicable totals.
     *
     * @param {string} itemType
     * @param {string} saleType
     * @param {string} whichTotal
     * @param {number} value
     */
    addToTotals(itemType, saleType, whichTotal, value) {
        this.validateSaleType(saleType);
        this.validateTotalType(whichTotal);

        // Create the totals[itemType] substructure.
        if (!this.totals.hasOwnProperty(itemType)) {
            this.totals[itemType] = this.makeBlankStatGroup();
        }

        // Create the totals[saleType] substructure.
        if (!this.totals.hasOwnProperty(saleType)) {
            this.totals[saleType] = this.makeBlankStatGroup();
        }

        // Create the totals[itemType][saleType] substructure.
        if (!this.totals[itemType].hasOwnProperty(saleType)) {
            this.totals[itemType][saleType] = this.makeBlankStatGroup();
        }

        this.addValueToStructure(whichTotal, value, this.totals);
        this.addValueToStructure(whichTotal, value, this.totals[itemType]);
        this.addValueToStructure(whichTotal, value, this.totals[itemType][saleType]);
        this.addValueToStructure(whichTotal, value, this.totals[saleType]);
    }

    validateSaleType(saleType) {
        if (Object.values(SalesTotals.SALE_TYPES).indexOf(saleType) === -1) {
            throw new Error(`Unknown sale type (${saleType})`);
        }
    }

    validateTotalType(whichTotal) {
        // We validate this by making sure it exists in the base totals.
        if (!this.totals.hasOwnProperty(whichTotal)) {
            throw new Error(`Unknown total type (${whichTotal})`);
        }
    }

    /**
     * @private
     *
     * Actually does the addition of `value` to `structure[whichTotal]`
     *
     * @param {string} whichTotal
     * @param {number} value
     * @param {object} structure
     */
    addValueToStructure(whichTotal, value, structure) {
        structure[whichTotal] = (new BigNumber(structure[whichTotal]))
            .plus(value)
            .toNumber();

        // This seems to be a hack to increment the order count if there are any non-zero qty lines in the cart.
        // The only time the order total is queried is in the cartService.isEmpty method
        if (whichTotal === 'lines' && structure.orders === 0) {
            ++structure.orders;
        }
    }

    /**
     * Get the total number of orders.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getOrders(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.ORDERS);
    }

    /**
     * Get the total item qty.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getQty(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.QTY);
    }

    /**
     * Get the total number of lines.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getLines(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.LINES);
    }

    /**
     * Get the base totals.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getBaseTotal(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.BASE_TOTAL);
    }

    /**
     * @param {string} [itemType]
     * @param {string} [saleType]
     *
     * @returns {number}
     */
    getBaseTotalPlusFees(itemType, saleType) {
        return new BigNumber(this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.BASE_TOTAL))
            .plus(this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.FEES))
            .toNumber();
    }

    /**
     * Get the discounts totals.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getDiscount(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.DISCOUNT);
    }

    /**
     * Get the line totals.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getLineTotal(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.LINE_TOTAL);
    }

    /**
     * Get the total number of people.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getPeopleTotal(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.PEOPLE);
    }

    /**
     * Get the gift aid totals.
     *
     * @param {?string} [itemType]
     * @param {?string} [saleType]
     *
     * @returns {number}
     */
    getGiftAidTotal(itemType, saleType) {
        return this.getTotal(itemType, saleType, SalesTotals.TOTAL_TYPES.GIFT_AID_DONATION);
    }

    /**
     * Reset the totals.
     */
    reset() {
        this.totals = this.makeBlankStatGroup();
    }

    /**
     * @param {CartLine|OrderLine} line
     */
    addLine(line) {
        const qty = line.getQuantity();
        if (qty === 0) {
            return;
        }

        const itemType = line.getItemType();
        const saleType = qty > 0 ? SalesTotals.SALE_TYPES.SALE : SalesTotals.SALE_TYPES.RETURN;

        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.LINES, 1);
        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.QTY, qty);
        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.BASE_TOTAL, line.baseTotal);
        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.FEES, line.bookingFeeSubtotal);
        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.DISCOUNT, line.discountAmount);
        this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.LINE_TOTAL, line.lineTotal);

        const people = line.getPeople();
        if (people !== null) {
            this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.PEOPLE, people);
        }

        if (line.giftAid) {
            // This may not be a correct calculation because it includes booking fees in the Gift Aid-able amount.
            this.addToTotals(itemType, saleType, SalesTotals.TOTAL_TYPES.GIFT_AID_DONATION, line.lineTotal);
        }
    }

    getHydrationMap() {
        return {
            // The totals object is just a plain object. Copy data as-is.
            totals: {},
        };
    }
}

SalesTotals.SALE_TYPES = {
    SALE: 'Sale',
    RETURN: 'Return',
};

SalesTotals.TOTAL_TYPES = {
    ORDERS: 'orders',
    QTY: 'qty',
    LINES: 'lines',
    BASE_TOTAL: 'baseTotal',
    FEES: 'fees',
    DISCOUNT: 'discount',
    LINE_TOTAL: 'lineTotal',
    PEOPLE: 'peopleTotal',
    GIFT_AID_DONATION: 'giftAidDonation',
};

module.exports = SalesTotals;
